import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { AppCard, AppBadge } from "../../components/ui";
import { FileDetailsModalProps } from "./types";
import { massPaymentApiService } from "../../services/api/masspayments/MassPaymentApiService";
import { GetFileProcessingErrorsResponse } from "../../services/api/masspayments/types";
import { utils } from "../../utils/utils";

const FileDetailsModal: React.FC<FileDetailsModalProps> = ({
  fileDetails,
  isOpen,
  onClose,
  onDownloadErrorLog,
  onDownloadLogs
}) => {
  const [transactionLogs, setTransactionLogs] = useState<any[]>([]);
  const [errorDetails, setErrorDetails] = useState<GetFileProcessingErrorsResponse | null>(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && fileDetails?.id) {
      loadData();
    } else if (!isOpen) {
      resetState();
    }
  }, [isOpen, fileDetails?.id]);

  const resetState = () => {
    setTransactionLogs([]);
    setErrorDetails(null);
    setIsLoadingLogs(false);
    setLogsError(null);
  };

  const loadData = async () => {
    if (!fileDetails?.id) return;

    setIsLoadingLogs(true);
    setLogsError(null);
    setTransactionLogs([]);
    setErrorDetails(null);

    try {
      const fileId = parseInt(fileDetails.id.toString());

      if (fileDetails.status === 'Processed') {
        // Load transaction logs for Processed status
        const result = await massPaymentApiService.getTransactionsFinalLogs({ fileId });

        if (result.success && result.data) {
          setTransactionLogs(result.data.transactionLogs || []);
        } else {
          setLogsError(result.message || 'Failed to load transaction logs');
        }
      } else {
        // Load error details for Uploaded and other statuses
        const result = await massPaymentApiService.getFileProcessingErrors({ fileId });

        if (result.success && result.data) {
          setErrorDetails(result.data);
        } else {
          setLogsError(result.message || 'Failed to load error details');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setLogsError('Error loading data');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const color = utils.getStatusBadgeColor(status) as any;
    return <AppBadge variant={color}>{status}</AppBadge>;
  };

  const renderDataSection = () => {
    if (isLoadingLogs) {
      return (
        <div className="relative overflow-hidden min-h-[80px] flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-300 dark:border-neutral-700 p-8">
          <div className="flex items-center justify-center whitespace-nowrap overflow-hidden text-base font-medium text-neutral-600 dark:text-neutral-400">
            <span>Loading transactions...</span>
          </div>
        </div>
      );
    }

    if (logsError) {
      return (
        <div className="text-center py-4 text-danger-600 dark:text-danger-400">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {logsError}
        </div>
      );
    }

    // Render transaction logs for Processed status
    if (fileDetails?.status === 'Processed') {
      if (transactionLogs.length === 0) {
        return (
          <div className="text-center py-4 text-neutral-500 dark:text-neutral-400">
            <i className="fas fa-info-circle mr-2"></i>
            No transaction logs available
          </div>
        );
      }

      return (
        <div className="max-h-[500px] overflow-y-auto border border-neutral-300 dark:border-neutral-700 rounded-md relative">
          <table className="w-full text-sm text-left">
            <thead className="bg-neutral-800 text-white sticky top-0 z-10">
              <tr>
                <th className="p-3 font-semibold" style={{ width: '60px' }}>#</th>
                <th className="p-3 font-semibold">Recipient Name</th>
                <th className="p-3 font-semibold">IBAN</th>
                <th className="p-3 font-semibold">BIC</th>
                <th className="p-3 font-semibold">Amount</th>
                <th className="p-3 font-semibold break-words" style={{ width: '200px', maxWidth: '200px' }}>Purpose</th>
                <th className="p-3 font-semibold" style={{ width: '150px' }}>Reference ID</th>
                <th className="p-3 font-semibold break-words" style={{ width: '200px', maxWidth: '200px' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {transactionLogs.map((log, index) => (
                <tr key={log.id} className="border-t border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 even:bg-neutral-50/50 dark:even:bg-neutral-800/30">
                  <td className="p-3">{index + 1}</td>
                  <td className="p-3">{log.recipientName || '-'}</td>
                  <td className="p-3">
                    {log.recipientIBAN ? (
                      <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded font-mono text-[0.85rem]">{log.recipientIBAN}</code>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="p-3">
                    {log.recipientBIC ? (
                      <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded font-mono text-[0.85rem]">{log.recipientBIC}</code>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="p-3">
                    {log.amount ? utils.formatAmountBGN(log.amount) : '-'}
                  </td>
                  <td className="p-3 break-words" style={{ width: '200px', maxWidth: '200px' }}>{log.purpose || '-'}</td>
                  <td className="p-3">
                    {log.flexcubeReference ? (
                      <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded font-mono text-[0.85rem]">{log.flexcubeReference}</code>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="p-3 break-words" style={{ width: '200px', maxWidth: '200px' }}>
                    {log.error ? (
                      <span className="text-danger-600 dark:text-danger-400">{log.error}</span>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Render transaction logs for Uploaded status (using error details data)
    if (fileDetails?.status === 'Uploaded' && errorDetails) {
      return (
        <div className="max-h-[500px] overflow-y-auto border border-neutral-300 dark:border-neutral-700 rounded-md relative">
          <table className="w-full text-sm text-left">
            <thead className="bg-neutral-800 text-white sticky top-0 z-10">
              <tr>
                <th className="p-3 font-semibold" style={{ width: '60px' }}>#</th>
                <th className="p-3 font-semibold" style={{ width: '200px', maxWidth: '200px' }}>Purpose</th>
                <th className="p-3 font-semibold" style={{ width: '100px' }}>Status</th>
                <th className="p-3 font-semibold" style={{ width: '150px' }}>Reference</th>
                <th className="p-3 font-semibold" style={{ width: '150px' }}>Timestamp</th>
                <th className="p-3 font-semibold" style={{ width: '200px', maxWidth: '200px' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {errorDetails.transactionsWithErrors && errorDetails.transactionsWithErrors.length > 0 ? (
                errorDetails.transactionsWithErrors.map((transaction: any, index: number) => (
                  <tr key={index} className="border-t border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 even:bg-neutral-50/50 dark:even:bg-neutral-800/30">
                    <td className="p-3">{index + 1}</td>
                    <td className="p-3 break-words" style={{ width: '200px', maxWidth: '200px' }}>{transaction.purpose || '-'}</td>
                    <td className="p-3">
                      <AppBadge variant="info">Uploaded</AppBadge>
                    </td>
                    <td className="p-3">
                      <span className="text-neutral-400">-</span>
                    </td>
                    <td className="p-3">
                      <small>{utils.formatDateTimeLong(errorDetails.uploadTime)}</small>
                    </td>
                    <td className="p-3 break-words" style={{ width: '200px', maxWidth: '200px' }}>
                      <span className="text-neutral-400">-</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-neutral-500 dark:text-neutral-400">
                    <i className="fas fa-info-circle mr-2"></i>
                    No transaction logs available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    // Render error details for other statuses
    if (errorDetails) {
      return (
        <div>
          {/* Error Summary */}
          <div className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-300 dark:border-neutral-700">
                <div className="mr-4 text-2xl">
                  <i className="fas fa-file-exclamation text-danger-500"></i>
                </div>
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">File Errors</div>
                  <div>
                    <AppBadge variant="danger">{errorDetails.totalFileLevelErrors}</AppBadge>
                  </div>
                </div>
              </div>
              <div className="flex items-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-300 dark:border-neutral-700">
                <div className="mr-4 text-2xl">
                  <i className="fas fa-table text-warning-500"></i>
                </div>
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">Transaction Errors</div>
                  <div>
                    <AppBadge variant="warning">{errorDetails.totalTransactionErrors}</AppBadge>
                  </div>
                </div>
              </div>
              <div className="flex items-center p-4 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-300 dark:border-neutral-700">
                <div className="mr-4 text-2xl">
                  <i className="fas fa-exclamation-triangle text-danger-500"></i>
                </div>
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">Total Errors</div>
                  <div>
                    <AppBadge variant="danger">{errorDetails.totalFileLevelErrors + errorDetails.totalTransactionErrors}</AppBadge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* File Level Errors */}
          {errorDetails.fileLevelErrors && errorDetails.fileLevelErrors.length > 0 && (
            <div className="mb-4">
              <h6 className="text-base font-semibold mb-4 text-neutral-600 dark:text-neutral-400">
                <i className="fas fa-file-exclamation mr-2 text-red-500"></i>
                File Level Errors
                <AppBadge variant="danger" className="ml-2">{errorDetails.totalFileLevelErrors}</AppBadge>
              </h6>
              <div className="max-h-[200px] overflow-y-auto">
                {errorDetails.fileLevelErrors.map((error: string, index: number) => (
                  <div key={index} className="flex items-start p-3 mb-2 bg-red-100 border border-red-200 rounded-md text-red-900">
                    <div className="mr-3 mt-0.5">
                      <i className="fas fa-times-circle text-danger-500"></i>
                    </div>
                    <div className="flex-1">
                      <span className="text-sm leading-relaxed">{error}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transaction Level Errors */}
          {errorDetails.transactionsWithErrors && errorDetails.transactionsWithErrors.length > 0 && (
            <div>
              <h6 className="text-base font-semibold mb-4 text-neutral-600 dark:text-neutral-400">
                <i className="fas fa-table mr-2 text-red-500"></i>
                Transaction Errors
                <AppBadge variant="warning" className="ml-2">{errorDetails.totalTransactionErrors}</AppBadge>
              </h6>
              <div className="max-h-[400px] overflow-y-auto border border-neutral-300 dark:border-neutral-700 rounded-md">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="border-0 p-3 font-semibold text-sm">#</th>
                      <th className="border-0 p-3 font-semibold text-sm">Recipient IBAN</th>
                      <th className="border-0 p-3 font-semibold text-sm">Recipient Name</th>
                      <th className="border-0 p-3 font-semibold text-sm">Amount</th>
                      <th className="border-0 p-3 font-semibold text-sm">Purpose</th>
                      <th className="border-0 p-3 font-semibold text-sm">BIC</th>
                      <th className="border-0 p-3 font-semibold text-sm">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorDetails.transactionsWithErrors.map((transaction: any, index: number) => (
                      <tr key={index}>
                        <td className="text-center p-3 align-middle border-t border-neutral-300 dark:border-neutral-700">
                          <AppBadge variant="neutral">{transaction.sequenceNo}</AppBadge>
                        </td>
                        <td className="p-3 align-middle border-t border-neutral-300 dark:border-neutral-700">
                          <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded font-mono text-[0.85rem]">{transaction.recipientIban}</code>
                        </td>
                        <td className="p-3 align-middle border-t border-neutral-300 dark:border-neutral-700">{transaction.recipientName}</td>
                        <td className="p-3 align-middle border-t border-neutral-300 dark:border-neutral-700 font-semibold text-green-600">{utils.formatAmountBGN(transaction.amount)}</td>
                        <td className="p-3 align-middle border-t border-neutral-300 dark:border-neutral-700">{transaction.purpose}</td>
                        <td className="p-3 align-middle border-t border-neutral-300 dark:border-neutral-700">{transaction.bic}</td>
                        <td className="p-3 align-middle border-t border-neutral-300 dark:border-neutral-700 max-w-[200px] break-words">
                          <span className="text-danger-600 dark:text-danger-400 text-sm leading-relaxed">{transaction.error}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="text-center py-4 text-neutral-500 dark:text-neutral-400">
        <i className="fas fa-info-circle mr-2"></i>
        No data available
      </div>
    );
  };

  if (!fileDetails) return null;

  const isFailedFile = utils.isFailedStatus(fileDetails.status || '');

  const footerContent = (
    <div className="flex justify-end gap-2">
      <Button
        label="Close"
        icon="pi pi-times"
        severity="secondary"
        onClick={onClose}
      />
      {(fileDetails.status === 'Processed') && (
        <Button
          label="Download Logs"
          icon="pi pi-download"
          onClick={() => onDownloadLogs(fileDetails.id, fileDetails.status)}
        />
      )}
      {(fileDetails.status !== 'Uploaded' && fileDetails.status !== 'Processed') && (
        <Button
          label="Download Error Log"
          icon="pi pi-download"
          severity="danger"
          onClick={() => onDownloadErrorLog(fileDetails.id)}
        />
      )}
    </div>
  );

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header={
        <span>
          <i className="fas fa-file-alt mr-2"></i>
          File Details - {fileDetails.fileName || fileDetails.content || 'Unknown File'}
        </span>
      }
      footer={footerContent}
      style={{ width: "90vw", maxWidth: "1200px" }}
      className="overflow-hidden"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <AppCard>
          <AppCard.Body>
            <h6 className="text-base font-semibold mb-3 text-neutral-700 dark:text-neutral-300">File Information</h6>
            <div>
              <div className="flex justify-between items-center py-2 border-b border-neutral-50 dark:border-neutral-900">
                <strong className="text-neutral-600 dark:text-neutral-400 min-w-[120px]">File Hash:</strong>
                <code className="bg-neutral-50 dark:bg-neutral-900 py-1 px-2 rounded font-mono text-[0.9rem] break-all">{fileDetails.hash || ''}</code>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-neutral-50 dark:border-neutral-900">
                <strong className="text-neutral-600 dark:text-neutral-400 min-w-[120px]">Payer IBAN:</strong>
                <span>{fileDetails.payerIban || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-neutral-50 dark:border-neutral-900">
                <strong className="text-neutral-600 dark:text-neutral-400 min-w-[120px]">Total Amount:</strong>
                <span className="font-semibold text-green-600 text-lg">
                  {utils.formatAmountBGN(fileDetails.totalAmount || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-neutral-50 dark:border-neutral-900">
                <strong className="text-neutral-600 dark:text-neutral-400 min-w-[120px]">Status:</strong>
                {getStatusBadge(fileDetails.status || 'Unknown')}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-neutral-50 dark:border-neutral-900">
                <strong className="text-neutral-600 dark:text-neutral-400 min-w-[120px]">Upload Time:</strong>
                <span>{utils.formatDateTimeLong(fileDetails.uploadTime)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <strong className="text-neutral-600 dark:text-neutral-400 min-w-[120px]">Transaction Count:</strong>
                <AppBadge variant="info">{fileDetails.totalTransactionCount || 0}</AppBadge>
              </div>
            </div>
          </AppCard.Body>
        </AppCard>
      </div>

      {(fileDetails.transactions || []).length > 0 && (
        <AppCard>
          <AppCard.Body>
            <h6 className="text-base font-semibold mb-3 text-neutral-700 dark:text-neutral-300">
              <i className="fas fa-table mr-2"></i>
              Transactions ({fileDetails.totalTransactionCount || 0})
            </h6>
            <div className="max-h-[400px] overflow-y-auto border border-neutral-200 dark:border-neutral-800 rounded-lg">
              <table className="table-fixed w-full text-sm text-left">
                <thead className="bg-neutral-100 dark:bg-neutral-800 sticky top-0">
                  <tr>
                    <th style={{ width: '5%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">#</th>
                    <th style={{ width: '20%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">Recipient</th>
                    <th style={{ width: '18%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">IBAN</th>
                    <th style={{ width: '12%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">Amount</th>
                    <th style={{ width: '15%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">Purpose</th>
                    <th style={{ width: '10%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">Status</th>
                    <th style={{ width: '20%' }} className="p-2 text-left font-semibold text-neutral-600 dark:text-neutral-400">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {(fileDetails.transactions || []).map((transaction: any, index: number) => (
                    <tr key={transaction.id || index} className="border-t border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{transaction.recipientName || '-'}</td>
                      <td className="p-2">
                        <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded font-mono text-[0.85rem]">{transaction.iban || '-'}</code>
                      </td>
                      <td className="p-2 font-semibold text-green-600">
                        {utils.formatAmountBGN(transaction.amount || 0)}
                      </td>
                      <td className="p-2">{transaction.purpose || '-'}</td>
                      <td className="p-2">{getStatusBadge(transaction.status || 'Unknown')}</td>
                      <td className="p-2">
                        {transaction.referenceNo ? (
                          <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded font-mono text-[0.85rem] text-neutral-600 dark:text-neutral-400">{transaction.referenceNo}</code>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AppCard.Body>
        </AppCard>
      )}

      <AppCard className="mt-4">
        <AppCard.Body>
          <h6 className="text-base font-semibold mb-3 text-neutral-700 dark:text-neutral-300">
            <i className={`fas ${(fileDetails.status === 'Processed' || fileDetails.status === 'Uploaded') ? 'fa-list-alt' : 'fa-exclamation-triangle'} mr-2`}></i>
            {(fileDetails.status === 'Processed' || fileDetails.status === 'Uploaded') ? 'Transaction Logs' : 'Error Details'}
            {(fileDetails.status === 'Processed' || fileDetails.status === 'Uploaded') && transactionLogs.length > 20 && (
              <small className="text-neutral-500 dark:text-neutral-400 ml-2">
                <i className="fas fa-info-circle mr-1"></i>
                Scroll to view all transactions
              </small>
            )}
          </h6>
          {renderDataSection()}
        </AppCard.Body>
      </AppCard>
    </Dialog>
  );
};

export default FileDetailsModal;
